import type { IntlShape } from "@formatjs/intl";
import {
  Alert,
  Button,
  ConfigProvider,
  DatePicker,
  Flex,
  Form,
  InputNumber,
  Modal,
  TimePicker,
  Typography,
} from "antd";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { combineDateAndTime } from "../utils/combineDateAndTime";
import styles from "./EditTimeEntryModal.module.css";

export type TimeEntryEditPayload = {
  started_at?: string;
  ended_at?: string;
};

type EditTimeEntryModalProps = {
  open: boolean;
  // Sessions are edited as two timestamps, task logs as a date plus the hours that
  // were originally logged against the task.
  mode: "session" | "task";
  startedAt?: string | null;
  endedAt?: string | null;
  title: string;
  technicianNames: string[];
  loading: boolean;
  // Field app: full-width touch targets, matching LogTimeModal.
  mobile?: boolean;
  intl: IntlShape;
  onSubmit: (payload: TimeEntryEditPayload) => void;
  onCancel: () => void;
};

type FormValues = {
  startDate: Dayjs | null;
  startTime: Dayjs | null;
  endDate: Dayjs | null;
  endTime: Dayjs | null;
  date: Dayjs | null;
  hours: number | null;
};

export const EditTimeEntryModal = ({
  open,
  mode,
  startedAt,
  endedAt,
  title,
  technicianNames,
  loading,
  mobile = false,
  intl,
  onSubmit,
  onCancel,
}: EditTimeEntryModalProps) => {
  const [form] = Form.useForm<FormValues>();

  const originalStart = startedAt ? dayjs(startedAt) : null;
  const originalEnd = endedAt ? dayjs(endedAt) : null;
  const isActiveSession = mode === "session" && originalEnd === null;

  const initialValues: FormValues = {
    startDate: originalStart,
    startTime: originalStart,
    endDate: originalEnd,
    endTime: originalEnd,
    date: originalEnd ?? originalStart,
    hours:
      originalStart && originalEnd
        ? Math.round(originalEnd.diff(originalStart, "minute") / 6) / 10
        : null,
  };

  const watchedStartDate = Form.useWatch("startDate", form);
  const watchedStartTime = Form.useWatch("startTime", form);
  const watchedEndDate = Form.useWatch("endDate", form);
  const watchedEndTime = Form.useWatch("endTime", form);

  const pendingStart = combineDateAndTime(watchedStartDate, watchedStartTime);
  const pendingEnd = combineDateAndTime(watchedEndDate, watchedEndTime);
  const endsBeforeStart =
    pendingStart != null && pendingEnd != null && !pendingEnd.isAfter(pendingStart);

  const handleSubmit = async () => {
    const values = await form.validateFields();

    if (mode === "session") {
      const nextStart = combineDateAndTime(values.startDate, values.startTime);
      if (nextStart === null) {
        return;
      }
      const nextEnd = combineDateAndTime(values.endDate, values.endTime);
      onSubmit({
        started_at: nextStart.toISOString(),
        // Closing an open session here would leave a row that reads as checked out
        // while carrying an empty check-out report, so the API refuses it. The end
        // fields are not rendered for an active session in the first place.
        ended_at: isActiveSession || nextEnd === null ? undefined : nextEnd.toISOString(),
      });
      return;
    }

    if (values.date === null || values.hours == null || originalEnd === null) {
      return;
    }
    // Keep the time of day the log was originally recorded at and move it to the
    // chosen date. Picking today can push that past the current time, which the API
    // rejects as incorrect_date, so clamp it rather than bounce the user.
    const now = dayjs();
    const movedEnd = values.date
      .hour(originalEnd.hour())
      .minute(originalEnd.minute())
      .second(0)
      .millisecond(0);
    const nextEnd = movedEnd.isAfter(now) ? now : movedEnd;
    onSubmit({
      started_at: nextEnd.subtract(Math.round(values.hours * 60), "minute").toISOString(),
      ended_at: nextEnd.toISOString(),
    });
  };

  return (
    <Modal
      open={open}
      title={title}
      onCancel={onCancel}
      footer={
        <Flex gap={8} justify={mobile ? undefined : "flex-end"}>
          <Button
            block={mobile}
            size={mobile ? "large" : undefined}
            className={mobile ? styles.footerButton : undefined}
            onClick={onCancel}
          >
            {intl.formatMessage({ id: "cancel" })}
          </Button>
          <Button
            block={mobile}
            size={mobile ? "large" : undefined}
            className={mobile ? styles.footerButton : undefined}
            type="primary"
            loading={loading}
            disabled={endsBeforeStart}
            onClick={handleSubmit}
          >
            {intl.formatMessage({ id: "confirm" })}
          </Button>
        </Flex>
      }
      destroyOnHidden
    >
      <ConfigProvider theme={mobile ? { token: { controlHeight: 44 } } : {}}>
        <Flex vertical gap={12} style={{ marginTop: 16 }}>
          {technicianNames.length > 1 && (
            <Alert
              type="info"
              showIcon
              message={intl.formatMessage(
                { id: "ticket_time_edit_cohort_notice" },
                { technicians: technicianNames.join(", ") }
              )}
            />
          )}
          <Form form={form} layout="vertical" initialValues={initialValues}>
            {mode === "session" ? (
              <>
                <Form.Item
                  name="startDate"
                  label={intl.formatMessage({ id: "ticket_time_edit_start_date" })}
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({ id: "ticket_time_edit_required" }),
                    },
                  ]}
                >
                  <DatePicker style={{ width: "100%" }} maxDate={dayjs()} />
                </Form.Item>
                <Form.Item
                  name="startTime"
                  label={intl.formatMessage({ id: "ticket_time_edit_start_time" })}
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({ id: "ticket_time_edit_required" }),
                    },
                  ]}
                >
                  <TimePicker format="HH:mm" style={{ width: "100%" }} />
                </Form.Item>
                {isActiveSession ? (
                  <Typography.Text type="secondary">
                    {intl.formatMessage({ id: "ticket_time_edit_active_note" })}
                  </Typography.Text>
                ) : (
                  <>
                    <Form.Item
                      name="endDate"
                      label={intl.formatMessage({ id: "ticket_time_edit_end_date" })}
                      rules={[
                        {
                          required: true,
                          message: intl.formatMessage({
                            id: "ticket_time_edit_required",
                          }),
                        },
                      ]}
                    >
                      <DatePicker style={{ width: "100%" }} maxDate={dayjs()} />
                    </Form.Item>
                    <Form.Item
                      name="endTime"
                      label={intl.formatMessage({ id: "ticket_time_edit_end_time" })}
                      style={{ marginBottom: 0 }}
                      validateStatus={endsBeforeStart ? "error" : undefined}
                      help={
                        endsBeforeStart
                          ? intl.formatMessage({
                              id: "ticket_time_edit_end_before_start",
                            })
                          : undefined
                      }
                      rules={[
                        {
                          required: true,
                          message: intl.formatMessage({
                            id: "ticket_time_edit_required",
                          }),
                        },
                      ]}
                    >
                      <TimePicker format="HH:mm" style={{ width: "100%" }} />
                    </Form.Item>
                  </>
                )}
              </>
            ) : (
              <>
                <Form.Item
                  name="date"
                  label={intl.formatMessage({ id: "ticket_time_edit_date" })}
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({ id: "ticket_time_edit_required" }),
                    },
                  ]}
                >
                  <DatePicker style={{ width: "100%" }} maxDate={dayjs()} />
                </Form.Item>
                <Form.Item
                  name="hours"
                  label={intl.formatMessage({ id: "ticket_time_edit_hours" })}
                  style={{ marginBottom: 0 }}
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({
                        id: "ticket_task_time_spent_required",
                      }),
                    },
                  ]}
                >
                  <InputNumber
                    min={0.5}
                    step={0.5}
                    precision={1}
                    addonAfter="h"
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </>
            )}
          </Form>
        </Flex>
      </ConfigProvider>
    </Modal>
  );
};
